import { AuditAction, AuditEntity, AuditEntry, CatalogReviewItem, ProtoDb } from '../models/domain.models';
import { hashOf } from './commission-math';
import { addDays } from './rng';
import { enrichCompliance } from './seed-compliance';
import { COMPANY, TREE_FEDERAL, TREE_RIOS } from './seed-named';
import { enrichImports } from './seed-portfolio';

const AUDIT_WINDOW_DAYS = 150;

function timeOf(key: string): string {
  const h = hashOf(key);
  return `${String(8 + (h % 10)).padStart(2, '0')}:${String(Math.floor(h / 10) % 60).padStart(2, '0')}`;
}

function auditLog(db: ProtoDb, today: string): AuditEntry[] {
  const since = addDays(today, -AUDIT_WINDOW_DAYS);
  const members = new Map(db.members.map(m => [m.oid, m]));
  const out: AuditEntry[] = [];
  const add = (
    key: string, memberOid: string | null, userName: string | null, action: AuditAction, entityType: AuditEntity,
    entityLabel: string, date: string, treeOid: string,
  ) => {
    if (date < since || date > today) return;
    out.push({
      oid: `aud-${key}`, treeOid, memberOid, userName: userName ?? 'Plataforma', action, entityType, entityLabel, date, time: timeOf(key),
    });
  };
  const treeOfMember = (oid: string) => members.get(oid)?.treeOid ?? TREE_RIOS;
  const nameOf = (oid: string) => members.get(oid)?.name ?? null;

  for (const q of db.quotes) add(q.oid, q.ownerOid, nameOf(q.ownerOid), 'quote-created', 'quote', q.number, q.createDate, treeOfMember(q.ownerOid));
  for (const p of db.policies.filter(x => x.origin === 'platform')) {
    add(p.oid, p.ownerOid, nameOf(p.ownerOid), 'policy-issued', 'policy', p.number, p.startDate, treeOfMember(p.ownerOid));
  }
  for (const e of db.endorsements) {
    add(e.oid, e.ownerOid, nameOf(e.ownerOid), 'endorsement-requested', 'endorsement', e.number, e.registerDate, treeOfMember(e.ownerOid));
  }
  for (const c of db.claims) add(c.oid, c.ownerOid, nameOf(c.ownerOid), 'claim-reported', 'claim', c.number, c.reportedDate, treeOfMember(c.ownerOid));
  for (const i of db.invitations) {
    add(i.oid, i.invitedByOid, nameOf(i.invitedByOid), 'invitation-sent', 'invitation', i.email, i.sentDate, treeOfMember(i.invitedByOid));
  }
  for (const m of db.members) {
    m.statusHistory.filter(h => h.status === 'suspended').forEach((h, n) => {
      add(`${m.oid}-susp-${n}`, h.byOid, h.byOid ? nameOf(h.byOid) : null, 'member-suspended', 'member', m.name, h.date, m.treeOid);
    });
  }
  return out.sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
}

function catalogReviews(today: string): CatalogReviewItem[] {
  const item = (
    n: number, companyOid: string, line: CatalogReviewItem['lineOfBusiness'], product: string, planName: string,
    daysAgo: number, suggestedLevel: string, coverages: [string, string][],
  ): CatalogReviewItem => ({
    oid: `cat-${String(n).padStart(3, '0')}`, companyOid, lineOfBusiness: line, product, planName,
    discoveredDate: addDays(today, -daysAgo), suggestedLevel,
    companyCoverages: coverages.map(([name, mappedTo]) => ({ name, mappedTo })),
    state: 'pending', confirmedLevel: null, decidedDate: null,
  });
  return [
    item(1, COMPANY.federal, 'auto', 'Automotores Flex', 'Flex Premium Plus', 2, 'Todo Riesgo con franquicia', [
      ['RC total obligatoria', 'Responsabilidad civil'], ['Hurto y robo total', 'Robo total'], ['Incendio y explosión', 'Incendio total'],
      ['Daños propios con franquicia', 'Daños parciales'], ['Granizo (solo carrocería)', 'Granizo'],
    ]),
    item(2, COMPANY.plata, 'home', 'Combinado Familiar Plus', 'Hogar Seguro Total', 4, 'Completo', [
      ['Incendio edificio', 'Incendio de edificio'], ['Incendio contenido', 'Incendio de contenido'],
      ['Robo y hurto', 'Robo en la vivienda'], ['Daño por agua', 'Daños por agua'], ['RC familiar', 'Responsabilidad civil hechos privados'],
    ]),
    item(3, COMPANY.nexo, 'commerce', 'Comercio Ágil', 'Ágil Integral', 5, 'Integral', [
      ['Incendio', 'Incendio'], ['Robo de mercadería', 'Robo de mercadería'], ['Pérdida de ganancias', 'Lucro cesante'],
    ]),
    item(4, COMPANY.litoral, 'life', 'Vida Protegido', 'Protegido Capital Ampliado', 6, 'Capital ampliado', [
      ['Fallecimiento', 'Muerte por cualquier causa'], ['Invalidez total y permanente', 'Invalidez total y permanente'],
      ['Diagnóstico de enfermedad crítica', 'Enfermedades graves'],
    ]),
    item(5, COMPANY.austral, 'auto', 'Automotores Clásico', 'Clásico Terceros Completo', 9, 'Terceros Completo', [
      ['RC obligatoria', 'Responsabilidad civil'], ['Robo total', 'Robo total'], ['Incendio total', 'Incendio total'],
      ['Auxilio mecánico 24 h', 'Auxilio mecánico'],
    ]),
  ];
}

/** Platform data derived from the joined seed without the RNG, so no tree's generated data moves. */
export function enrichPlatform(db: ProtoDb, today: string): void {
  db.auditLog = auditLog(db, today);
  db.catalogReviews = catalogReviews(today);
  enrichImports(db, today);
  enrichCompliance(db, today);
  db.supportAccesses = [
    {
      oid: 'sup-0001', adminName: 'Soporte Plataforma', treeOid: TREE_RIOS,
      reason: 'Consulta del productor por una diferencia en una liquidación de comisión',
      startDate: addDays(today, -41), startTime: '10:12', endDate: addDays(today, -41), endTime: '10:40',
    },
    {
      oid: 'sup-0002', adminName: 'Soporte Plataforma', treeOid: TREE_FEDERAL,
      reason: 'Revisión de credenciales de Pampa Mutual solicitada por el productor raíz',
      startDate: addDays(today, -12), startTime: '15:03', endDate: addDays(today, -12), endTime: '15:21',
    },
  ];
}
